<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PortfolioLabels from './PortfolioLabels.vue';
import messages from './messages';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  selected: { type: Object, default: null },
  meta: { type: Object, default: () => ({}) },
  scope: { type: String, default: 'mine' },
  query: { type: String, default: '' },
  loading: Boolean,
  error: Boolean,
  historyLoading: Boolean,
  historyError: Boolean,
  conversations: { type: Array, default: () => [] },
  filters: { type: Object, default: () => ({}) },
  sort: { type: String, default: 'name' },
  direction: { type: String, default: 'asc' },
  labels: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
});
const emit = defineEmits([
  'scope',
  'search',
  'select',
  'page',
  'retry',
  'open',
  'conversation',
  'filters',
  'sort',
  'toggle',
  'toggleAll',
  'clearSelection',
  'labels',
  'portfolio',
  'create',
]);
const { t, locale } = useI18n({ useScope: 'local', messages });
const mobileDetail = ref(false);
const optionsDialog = ref(null);
const optionsContact = ref(null);
const columns = [
  { key: 'contact', sort: 'name' },
  { key: 'owner', sort: 'owner_name' },
  { key: 'seller', sort: 'last_order_seller' },
  { key: 'interaction', sort: 'last_commercial_at' },
  { key: 'expiry', sort: 'expires_at' },
];
const filter = (key, value) =>
  emit('filters', { ...props.filters, [key]: value });
const showOptions = contact => {
  optionsContact.value = contact;
  optionsDialog.value.open();
};
const optionAction = action => {
  optionsDialog.value.close();
  emit(action, optionsContact.value);
};
const editPortfolio = contact => emit('portfolio', contact);
const portfolio = computed(() => props.selected?.portfolio || {});
const tabs = computed(() =>
  props.meta.administrative
    ? ['all', 'mine', 'available']
    : ['mine', 'available']
);
const stateText = state => {
  if (state === 'available') return t('availableState');
  if (state === 'owned') return t('owned');
  return t('unclassified');
};
const ownerOptions = computed(() => [
  { value: '', label: t('allOwners') },
  { value: 'unassigned', label: t('unowned') },
  ...(props.meta.owners || []).map(owner => ({
    value: String(owner.id),
    label: owner.name,
  })),
]);
const stateOptions = computed(() => [
  { value: '', label: t('allStates') },
  ...['owned', 'available', 'unclassified'].map(state => ({
    value: state,
    label: stateText(state),
  })),
]);
const labelOptions = computed(() => [
  { value: '', label: t('allLabels') },
  ...props.labels.map(label => ({ value: label.title, label: label.title })),
]);
const sortOptions = computed(() =>
  columns
    .filter(column => ['contact', 'interaction', 'expiry'].includes(column.key))
    .map(column => ({ value: column.sort, label: t(column.key) }))
);
const stateClass = state => {
  if (state === 'available') return 'bg-n-teal-3 text-n-teal-12';
  if (state === 'owned') return 'bg-n-blue-3 text-n-blue-12';
  return 'bg-n-slate-3 text-n-slate-12';
};
const isExpired = value => value && new Date(value).getTime() <= Date.now();
const date = value =>
  value
    ? new Intl.DateTimeFormat(locale.value.replace('_', '-')).format(
        new Date(value)
      )
    : t('none');
const daysSince = value =>
  Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000));
const activity = contact =>
  contact.portfolio?.last_commercial_at
    ? t('days', { days: daysSince(contact.portfolio.last_commercial_at) })
    : t('noInteraction');
const expiryLabel = computed(() => {
  if (!portfolio.value.expires_at) return '';
  const days = Math.ceil(
    (new Date(portfolio.value.expires_at).getTime() - Date.now()) / 86400000
  );
  return days > 0 ? t('remaining', { days }) : t('overdue');
});
const currentAssignees = computed(
  () =>
    [
      ...new Set(
        props.conversations
          .filter(c => c.status !== 'resolved')
          .map(c => c.meta?.assignee?.name)
          .filter(Boolean)
      ),
    ].join(', ') || t('unowned')
);
const select = contact => {
  mobileDetail.value = true;
  emit('select', contact);
};
const changeScope = scope => {
  mobileDetail.value = false;
  emit('scope', scope);
};
</script>

<template>
  <section
    class="flex flex-col flex-1 min-w-0 h-full overflow-hidden bg-n-background text-n-slate-12"
  >
    <header class="px-5 pt-5 border-b border-n-weak bg-n-solid-2 lg:px-8">
      <div class="flex items-center justify-between gap-3">
        <h1 class="m-0 text-2xl font-semibold tracking-tight">
          {{ t('title') }}
        </h1>
        <Button
          :label="t('newContact')"
          icon="i-lucide-plus"
          @click="emit('create')"
        />
      </div>
      <nav class="flex gap-5 mt-4 overflow-x-auto" :aria-label="t('title')">
        <button
          v-for="tab in tabs"
          :key="tab"
          type="button"
          class="px-1 py-3 text-sm font-medium whitespace-nowrap border-b-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            scope === tab
              ? 'border-n-brand text-n-slate-12'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          :aria-current="scope === tab ? 'page' : undefined"
          @click="changeScope(tab)"
        >
          {{ t(tab) }}
          <span
            v-if="meta.counts?.[tab] !== undefined"
            class="ml-1 tabular-nums"
            >({{ meta.counts[tab] }})</span
          >
        </button>
      </nav>
    </header>
    <div class="flex flex-1 min-h-0">
      <div
        class="flex flex-col min-w-0 w-full border-r border-n-weak"
        :class="[
          scope === 'all' ? '' : 'lg:w-[44%] lg:max-w-[660px]',
          mobileDetail && selected && scope !== 'all'
            ? 'hidden lg:flex'
            : 'flex',
        ]"
      >
        <div
          class="px-5 lg:px-7"
          :class="scope === 'all' ? 'py-3' : 'pt-5 pb-4'"
        >
          <p
            class="text-sm text-n-slate-11"
            :class="scope === 'all' ? 'mb-3' : 'mb-4'"
          >
            {{ t(`${scope}Hint`) }}
          </p>
          <div
            :class="scope === 'all' ? 'xl:flex xl:items-center xl:gap-2' : ''"
          >
            <label
              class="flex min-w-0 items-center gap-3 px-3 rounded-lg bg-n-field border border-n-weak focus-within:ring-2 focus-within:ring-n-brand"
              :class="scope === 'all' ? 'xl:flex-1' : ''"
            >
              <span
                class="i-lucide-search size-4 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <input
                :value="query"
                type="search"
                :aria-label="t('search')"
                :placeholder="t('search')"
                class="!m-0 !border-0 !outline-none !bg-transparent !shadow-none !ring-0 !px-0 w-full text-sm placeholder:!text-n-slate-11"
                :class="scope === 'all' ? '!h-10' : '!h-11'"
                @input="emit('search', $event.target.value)"
              />
            </label>
            <div
              class="flex flex-wrap gap-2"
              :class="scope === 'all' ? 'mt-3 xl:mt-0 xl:w-3/5' : 'mt-3'"
            >
              <div class="min-w-0 max-w-full flex-1 basis-full lg:basis-0">
                <span
                  class="hidden mb-1 text-xs text-n-slate-11 sm:block lg:hidden"
                >
                  {{ t('responsible') }}
                </span>
                <ComboBox
                  :model-value="String(filters.owner_id || '')"
                  :options="ownerOptions"
                  :allow-deselect="false"
                  :aria-label="t('responsible')"
                  @update:model-value="filter('owner_id', $event)"
                />
              </div>
              <div class="min-w-0 max-w-full flex-1 basis-full lg:basis-0">
                <span
                  class="hidden mb-1 text-xs text-n-slate-11 sm:block lg:hidden"
                >
                  {{ t('stateFilter') }}
                </span>
                <ComboBox
                  :model-value="filters.state || ''"
                  :options="stateOptions"
                  :allow-deselect="false"
                  :aria-label="t('stateFilter')"
                  @update:model-value="filter('state', $event)"
                />
              </div>
              <div class="min-w-0 max-w-full flex-1 basis-full lg:basis-0">
                <span
                  class="hidden mb-1 text-xs text-n-slate-11 sm:block lg:hidden"
                >
                  {{ t('labels') }}
                </span>
                <ComboBox
                  :model-value="filters.label || ''"
                  :options="labelOptions"
                  :allow-deselect="false"
                  :aria-label="t('labels')"
                  @update:model-value="filter('label', $event)"
                />
              </div>
              <div
                v-if="filters.owner_id || filters.state || filters.label"
                class="basis-full lg:basis-auto"
              >
                <Button
                  :label="t('clearFilters')"
                  ghost
                  slate
                  @click="
                    emit('filters', { owner_id: '', state: '', label: '' })
                  "
                />
              </div>
            </div>
          </div>
          <div
            v-if="selectedIds.length"
            class="flex flex-wrap items-center gap-3 mt-3 p-3 rounded-lg border border-n-weak bg-n-solid-2"
          >
            <span role="status" class="text-sm">{{
              t('selectedCount', { count: selectedIds.length })
            }}</span>
            <Button
              :label="t('addLabels')"
              icon="i-lucide-tags"
              sm
              @click="emit('labels')"
            />
            <Button
              :label="t('clearSelection')"
              ghost
              slate
              sm
              @click="emit('clearSelection')"
            />
          </div>
        </div>
        <div
          v-if="loading"
          role="status"
          class="p-8 text-center text-n-slate-11"
        >
          {{ t('loading') }}
        </div>
        <div v-else-if="error" role="alert" class="p-8 text-center">
          <p>{{ t('error') }}</p>
          <button class="text-n-slate-12 underline p-3" @click="emit('retry')">
            {{ t('retry') }}
          </button>
        </div>
        <div
          v-else-if="!contacts.length"
          class="p-8 text-center text-n-slate-11"
        >
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('empty') }}
          </h2>
          <p class="text-sm">{{ t('emptyHint') }}</p>
        </div>
        <div
          v-else-if="scope === 'all'"
          class="isolate flex-1 overflow-auto mx-5 mb-4 rounded-xl border border-n-weak bg-n-solid-2 lg:mx-7"
        >
          <table class="w-full text-sm text-left whitespace-nowrap">
            <thead class="sticky top-0 z-10 bg-n-solid-2 text-n-slate-11">
              <tr>
                <th class="px-3 py-3 border-b border-n-weak">
                  <input
                    type="checkbox"
                    :aria-label="t('selectPage')"
                    :checked="selectedIds.length === contacts.length"
                    :indeterminate="
                      selectedIds.length > 0 &&
                      selectedIds.length < contacts.length
                    "
                    class="!m-0"
                    @change="emit('toggleAll', $event.target.checked)"
                  />
                </th>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="px-3 py-3 font-medium border-b border-n-weak"
                  :aria-sort="
                    sort === column.sort
                      ? direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  "
                >
                  <button
                    class="flex items-center gap-2 py-1 focus-visible:ring-2 focus-visible:ring-n-brand"
                    @click="emit('sort', column.sort)"
                  >
                    {{ t(column.key)
                    }}<span
                      class="size-3.5"
                      :class="
                        sort === column.sort
                          ? direction === 'asc'
                            ? 'i-lucide-arrow-up'
                            : 'i-lucide-arrow-down'
                          : 'i-lucide-chevrons-up-down'
                      "
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th class="px-3 py-3 font-medium border-b border-n-weak">
                  {{ t('labels') }}
                </th>
                <th class="border-b border-n-weak">
                  <span class="sr-only">{{ t('edit') }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contact in contacts"
                :key="contact.id"
                class="border-b border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2">
                  <input
                    type="checkbox"
                    :aria-label="t('selectContact', { name: contact.name })"
                    :checked="selectedIds.includes(contact.id)"
                    class="!m-0"
                    @change="emit('toggle', contact.id)"
                  />
                </td>
                <td class="px-3 py-2">
                  <button
                    class="flex gap-3 items-center text-left focus-visible:ring-2 focus-visible:ring-n-brand rounded"
                    @click="emit('open', contact)"
                  >
                    <Avatar
                      :name="contact.name"
                      :src="contact.thumbnail"
                      :size="36"
                      rounded-full
                    /><span
                      ><span class="block font-medium">{{ contact.name }}</span
                      ><span class="block text-xs text-n-slate-11">{{
                        contact.phone_number || contact.email
                      }}</span></span
                    >
                  </button>
                </td>
                <td class="px-3 py-2">
                  <span class="block">{{
                    contact.portfolio?.owner_name || t('unowned')
                  }}</span
                  ><span
                    class="inline-block mt-1 rounded-full px-2 py-0.5 text-xs"
                    :class="stateClass(contact.portfolio?.state)"
                    >{{ stateText(contact.portfolio?.state) }}</span
                  >
                </td>
                <td class="px-3 py-2">
                  {{ contact.portfolio?.last_order_seller || t('none') }}
                </td>
                <td class="px-3 py-2">
                  {{ date(contact.portfolio?.last_commercial_at) }}
                </td>
                <td class="px-3 py-2">
                  {{ date(contact.portfolio?.expires_at) }}
                </td>
                <td class="px-3 py-2">
                  <PortfolioLabels :labels="contact.labels" />
                </td>
                <td class="px-2 py-2">
                  <Button
                    icon="i-lucide-ellipsis-vertical"
                    :aria-label="t('options', { name: contact.name })"
                    ghost
                    slate
                    @click="showOptions(contact)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="flex-1 min-h-0 overflow-y-auto px-4 lg:px-5">
          <div
            class="flex items-center justify-between gap-2 py-2 border-b border-n-weak"
          >
            <label class="flex items-center gap-2 text-xs text-n-slate-11"
              ><input
                type="checkbox"
                :aria-label="t('selectPage')"
                :checked="selectedIds.length === contacts.length"
                class="!m-0"
                @change="emit('toggleAll', $event.target.checked)"
              />{{ t('selectPage') }}</label
            >
            <ComboBox
              :model-value="sort"
              :options="sortOptions"
              :allow-deselect="false"
              :aria-label="t('sortBy')"
              class="!w-48 max-w-[60%]"
              @update:model-value="emit('sort', $event)"
            />
          </div>
          <div
            v-for="contact in contacts"
            :key="contact.id"
            class="flex items-center w-full gap-2 px-3 py-4 my-2 rounded-xl border border-n-weak"
            :class="
              selected?.id === contact.id
                ? 'bg-n-blue-2 ring-1 ring-n-brand/30'
                : 'bg-n-solid-2 hover:bg-n-slate-2'
            "
          >
            <input
              type="checkbox"
              :aria-label="t('selectContact', { name: contact.name })"
              :checked="selectedIds.includes(contact.id)"
              class="!m-0 shrink-0"
              @change="emit('toggle', contact.id)"
            />
            <button
              class="flex items-center flex-1 min-w-0 gap-3 text-left rounded focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-pressed="selected?.id === contact.id"
              @click="select(contact)"
            >
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="48"
                rounded-full
              />
              <span class="flex-1 min-w-0">
                <span class="block font-medium truncate">{{
                  contact.name
                }}</span>
                <span class="block mt-1 text-xs leading-5 text-n-slate-11">{{
                  activity(contact)
                }}</span>
                <span class="block text-xs text-n-slate-11 truncate">{{
                  contact.phone_number || contact.email
                }}</span>
                <PortfolioLabels class="mt-2" :labels="contact.labels" />
              </span>
              <span
                class="hidden xl:block rounded-full px-2 py-1 text-xs max-w-28 text-center"
                :class="stateClass(contact.portfolio?.state)"
                >{{ stateText(contact.portfolio?.state) }}</span
              >
              <span
                class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-10"
                aria-hidden="true"
              />
            </button>
            <Button
              icon="i-lucide-ellipsis-vertical"
              :aria-label="t('options', { name: contact.name })"
              ghost
              slate
              @click="showOptions(contact)"
            />
          </div>
        </div>
        <footer
          v-if="!error && !loading"
          class="flex items-center justify-between gap-2 px-5 py-4 text-xs text-n-slate-11 border-t border-n-weak"
        >
          <span aria-live="polite">{{
            t('pagination', { count: meta.count || 0, page: meta.page || 1 })
          }}</span>
          <div class="flex gap-2">
            <button
              :disabled="loading || (meta.page || 1) <= 1"
              :aria-label="t('prev')"
              class="p-2 rounded hover:bg-n-alpha-2 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="emit('page', meta.page - 1)"
            >
              <span class="i-lucide-chevron-left block size-4" /></button
            ><button
              :disabled="
                loading ||
                (meta.page || 1) * (meta.per_page || 25) >= (meta.count || 0)
              "
              :aria-label="t('next')"
              class="p-2 rounded hover:bg-n-alpha-2 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="emit('page', meta.page + 1)"
            >
              <span class="i-lucide-chevron-right block size-4" />
            </button>
          </div>
        </footer>
      </div>
      <article
        v-if="scope !== 'all' && selected && !loading && !error"
        class="flex-1 min-w-0 overflow-y-auto bg-n-solid-2 p-5 lg:p-7"
        :class="mobileDetail ? 'block' : 'hidden lg:block'"
      >
        <button
          class="lg:hidden flex gap-2 items-center mb-5 p-2 text-sm text-n-slate-12"
          @click="mobileDetail = false"
        >
          <span class="i-lucide-arrow-left size-4" />{{ t('back') }}
        </button>
        <div
          class="flex flex-wrap items-center gap-4 pb-6 border-b border-n-weak"
        >
          <Avatar
            :name="selected.name"
            :src="selected.thumbnail"
            :size="72"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <h2 class="m-0 text-xl font-semibold break-words">
              {{ selected.name }}
            </h2>
            <span
              class="inline-block mt-2 px-3 py-1.5 rounded-full text-xs font-medium"
              :class="stateClass(portfolio.state)"
              >{{ stateText(portfolio.state) }}</span
            >
          </div>
          <PortfolioLabels class="basis-full" :labels="selected.labels" />
          <Button
            :label="t('editLabels')"
            icon="i-lucide-tags"
            ghost
            slate
            @click="emit('labels', selected)"
          />
          <Button
            v-if="meta.administrative"
            :label="t('editPortfolio')"
            icon="i-lucide-briefcase-business"
            ghost
            slate
            @click="editPortfolio(selected)"
          />
          <Button
            :label="t('historyButton')"
            icon="i-lucide-contact"
            slate
            outline
            class="basis-full sm:basis-auto"
            @click="emit('open', selected)"
          />
        </div>
        <dl
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-7 py-6 border-b border-n-weak"
        >
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('owner') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{ portfolio.owner_name || t('unowned') }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('seller') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{ portfolio.last_order_seller || t('none') }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('current') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{
                historyLoading
                  ? t('loading')
                  : historyError
                    ? t('historyError')
                    : currentAssignees
              }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('interaction') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{ date(portfolio.last_commercial_at) }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('expiry') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{ date(portfolio.expires_at)
              }}<span
                class="inline-block mt-1 rounded px-2 py-1 text-xs"
                :class="
                  isExpired(portfolio.expires_at)
                    ? 'bg-n-ruby-3 text-n-ruby-12'
                    : 'text-n-slate-11'
                "
                >{{ expiryLabel }}</span
              >
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">{{ t('order') }}</dt>
            <dd class="m-0 mt-2 text-sm font-medium">
              {{ date(portfolio.last_order_at) }}
            </dd>
          </div>
        </dl>
        <p
          class="flex items-start gap-2 p-3 my-5 rounded-lg bg-n-slate-2 dark:bg-n-solid-3 text-xs leading-5 text-n-slate-11"
        >
          <span class="i-lucide-info size-4 shrink-0 mt-0.5" />{{
            t('readOnly')
          }}
        </p>
        <h3 class="text-base font-semibold">{{ t('history') }}</h3>
        <p
          v-if="historyLoading"
          role="status"
          class="py-4 text-sm text-n-slate-11"
        >
          {{ t('loading') }}
        </p>
        <p
          v-else-if="historyError"
          role="alert"
          class="py-4 text-sm text-n-slate-11"
        >
          {{ t('historyError') }}
        </p>
        <p
          v-else-if="!conversations.length"
          class="py-4 text-sm text-n-slate-11"
        >
          {{ t('historyEmpty') }}
        </p>
        <div v-else class="divide-y divide-n-weak">
          <button
            v-for="conversation in conversations"
            :key="conversation.id"
            class="flex items-center gap-3 w-full py-4 text-left hover:bg-n-alpha-1 focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="emit('conversation', conversation)"
          >
            <span
              class="i-lucide-message-circle size-5 shrink-0 text-n-slate-11"
            /><span class="flex-1 text-sm"
              ><span class="block font-medium">{{
                t('conversation', { id: conversation.id })
              }}</span
              ><span class="text-xs text-n-slate-11">{{
                conversation.meta?.assignee?.name || t('unowned')
              }}</span></span
            ><span class="text-xs text-n-slate-11">{{
              t(conversation.status)
            }}</span>
          </button>
        </div>
        <p
          class="mt-7 pt-4 border-t border-n-weak text-xs leading-5 text-n-slate-11"
        >
          {{ t('policy') }}
        </p>
      </article>
    </div>
  </section>
  <Dialog
    ref="optionsDialog"
    :aria-label="t('options', { name: optionsContact?.name || '' })"
    :title="optionsContact?.name || ''"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-2">
      <Button
        :label="t('edit')"
        icon="i-lucide-contact"
        ghost
        slate
        @click="optionAction('open')"
      />
      <Button
        :label="t('editLabels')"
        icon="i-lucide-tags"
        ghost
        slate
        @click="optionAction('labels')"
      />
      <Button
        v-if="meta.administrative"
        :label="t('editPortfolio')"
        icon="i-lucide-briefcase-business"
        ghost
        slate
        @click="optionAction('portfolio')"
      />
    </div>
  </Dialog>
</template>
